import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { isStaging, isDev } from '@/lib/utils';
import { useLocation } from 'react-router-dom';

interface MetaHeadProps {
  title?: string;
  description?: string;
  image?: string;
  noindex?: boolean;
  canonical?: string;
  children?: React.ReactNode;
}

export function MetaHead({
  title,
  description,
  image = 'https://cdn-site.clarymind.com/clarymind-tw-cover.jpg',
  noindex = false,
  canonical,
  children
}: MetaHeadProps) {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  // Get default meta content from translations if not provided
  const metaTitle = title || t('meta.defaultTitle');
  const metaDescription = description || t('meta.defaultDescription');
  const metaImage = image || t('assets.cover');

  // Automatically set noindex for staging and dev environments
  const shouldNoindex = noindex || isStaging() || isDev();

  // Generate URLs for hreflang and canonical
  const getBaseUrl = () => {
    if (typeof window === 'undefined') return '';
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Use the appropriate domain based on environment
    if (isStaging()) {
      return `${protocol}//staging.join.clarymind.com`;
    } else if (isDev()) {
      return `${protocol}//dev.join.clarymind.com`;
    } else if (hostname.includes('localhost')) {
      return `${protocol}//${hostname}:${window.location.port}`;
    } else {
      return `${protocol}//join.clarymind.com`;
    }
  };

  const generateHreflangUrls = () => {
    const baseUrl = getBaseUrl();
    const currentPath = location.pathname;

    // Use the same robust strategy as getLanguagePath to clean the path
    // Remove any existing language prefix and leading/trailing slashes
    const cleanPath = currentPath.replace(/^\/[^/]+\/|^\/|\/$/g, '');
    const basePath = cleanPath ? `/${cleanPath}` : '/product/clarymind-app';

    return {
      en: `${baseUrl}${basePath}`,
      'zh-TW': `${baseUrl}/zh-TW${basePath}`,
      ko: `${baseUrl}/ko${basePath}`,
      ja: `${baseUrl}/ja${basePath}`,
    };
  };

  const hreflangUrls = generateHreflangUrls();

  // Generate canonical URL
  const canonicalUrl = canonical || hreflangUrls[i18n.language as keyof typeof hreflangUrls] || hreflangUrls.en;

  return (
    <Helmet>
      {/* HTML lang attribute */}
      <html lang={i18n.language} />

      {/* Basic meta tags */}
      <title>{metaTitle}</title>
      <meta name="description" content={metaDescription} />
      <meta name="author" content="Clarymind" />
      <meta name="language" content={i18n.language} />
      {shouldNoindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Hreflang tags for multilingual SEO */}
      <link rel="alternate" hrefLang="en" href={hreflangUrls.en} />
      <link rel="alternate" hrefLang="zh-TW" href={hreflangUrls['zh-TW']} />
      <link rel="alternate" hrefLang="zh-Hant" href={hreflangUrls['zh-TW']} />
      <link rel="alternate" hrefLang="ko" href={hreflangUrls.ko} />
      <link rel="alternate" hrefLang="ja" href={hreflangUrls.ja} />
      <link rel="alternate" hrefLang="x-default" href={hreflangUrls.en} />

      {/* Open Graph meta tags */}
      <meta property="og:title" content={metaTitle} />
      <meta property="og:description" content={metaDescription} />
      <meta property="og:image" content={metaImage} />
      <meta property="og:type" content="website" />
      <meta property="og:locale" content={i18n.language} />

      {/* Twitter meta tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:site" content="@clarymind" />
      <meta name="twitter:title" content={metaTitle} />
      <meta name="twitter:description" content={metaDescription} />
      <meta name="twitter:image" content={metaImage} />

      {/* Allow additional meta tags to be injected */}
      {children}
    </Helmet>
  );
}